/**
 * Image optimization utilities for better performance
 */

/**
 * Optimize Firebase Storage image URLs with resizing and format conversion
 * @param url - Original image URL
 * @param width - Desired width (default: 400px for story cards)
 * @param format - Desired format (default: webp)
 * @returns Optimized image URL
 */
export const optimizeImageUrl = (
  url: string,
  width: number = 400,
  format: "webp" | "jpeg" | "png" = "webp"
): string => {
  if (!url) return "";

  // If Firebase Storage URL
  if (url.includes("firebasestorage.googleapis.com")) {
    try {
      const urlObj = new URL(url);
      
      // Add or update query parameters for optimization
      urlObj.searchParams.set("w", width.toString());
      urlObj.searchParams.set("fm", format);
      urlObj.searchParams.set("q", "80"); // Quality 80%
      
      return urlObj.toString();
    } catch (e) {
      console.warn("Failed to optimize image URL:", e);
      return url;
    }
  }

  // If Cloudinary URL (future support)
  if (url.includes("cloudinary.com")) {
    try {
      // Cloudinary URL pattern: https://res.cloudinary.com/[cloud]/image/upload/[transformations]/[image]
      const parts = url.split("/upload/");
      if (parts.length === 2) {
        const transformations = `f_auto,q_auto,w_${width}`;
        return `${parts[0]}/upload/${transformations}/${parts[1]}`;
      }
    } catch (e) {
      console.warn("Failed to optimize Cloudinary URL:", e);
      return url;
    }
  }

  // Return original URL if not a supported CDN
  return url;
};

/**
 * Get srcset for responsive images
 * @param url - Original image URL
 * @param sizes - Array of widths for srcset
 * @returns srcset string
 */
export const getResponsiveSrcSet = (
  url: string,
  sizes: number[] = [400, 800, 1200]
): string => {
  if (!url) return "";

  return sizes
    .map((size) => {
      const optimizedUrl = optimizeImageUrl(url, size);
      return `${optimizedUrl} ${size}w`;
    })
    .join(", ");
};

/**
 * Get sizes attribute for responsive images
 * @param maxWidth - Maximum width in viewport
 * @returns sizes string
 */
export const getResponsiveSizes = (maxWidth: string = "400px"): string => {
  return `(max-width: 600px) 120px, (max-width: 900px) 150px, ${maxWidth}`;
};

/**
 * Preload critical images
 * @param imageUrls - Array of image URLs to preload
 * @param priority - Whether to mark as high priority
 */
export const preloadImages = (imageUrls: string[], priority: boolean = false) => {
  if (typeof window === "undefined") return;

  imageUrls.forEach((url) => {
    if (!url) return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = optimizeImageUrl(url, 400);

    if (priority) {
      link.setAttribute("fetchpriority", "high");
    }

    document.head.appendChild(link);
  });
};

/**
 * Check if image is loaded
 * @param url - Image URL
 * @returns Promise that resolves when image is loaded
 */
export const isImageLoaded = (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    if (!url) {
      resolve(false);
      return;
    }

    const img = new Image();
    img.onload = () => resolve(true);
    img.onerror = () => resolve(false);
    img.src = url;
  });
};

/**
 * Get optimal image width based on device pixel ratio
 * @param baseWidth - Base width in CSS pixels
 * @returns Optimal width for the device
 */
export const getOptimalImageWidth = (baseWidth: number): number => {
  if (typeof window === "undefined") return baseWidth;

  const dpr = window.devicePixelRatio || 1;
  
  // Cap at 2x to avoid loading overly large images
  const multiplier = Math.min(dpr, 2);
  
  return Math.round(baseWidth * multiplier);
};

/**
 * Compress image using browser-image-compression library
 * @param file - Image file to compress
 * @param maxWidth - Maximum width (default: 1920)
 * @param maxHeight - Maximum height (default: 1920)
 * @param quality - Compression quality 0-1 (default: 0.8)
 * @returns Compressed file
 */
export const compressImage = async (
  file: File,
  maxWidth: number = 1920,
  maxHeight: number = 1920,
  quality: number = 0.8
): Promise<File> => {
  // Dynamic import to avoid loading the library if not needed
  const imageCompression = await import("browser-image-compression");
  
  const options = {
    maxSizeMB: 1, // Max file size in MB
    maxWidthOrHeight: Math.max(maxWidth, maxHeight),
    useWebWorker: true,
    fileType: "image/jpeg", // Convert to JPEG for better compression
    initialQuality: quality,
  };

  try {
    return await imageCompression.default(file, options);
  } catch (error) {
    console.error("Image compression failed:", error);
    // Return original file if compression fails
    return file;
  }
};

/**
 * Generate a blur placeholder data URL from an image
 * Creates a very small, low-quality version of the image for blur effect
 * @param imageUrl - Source image URL
 * @param width - Width of placeholder (default: 20)
 * @returns Promise resolving to data URL string
 */
export const generateBlurPlaceholder = async (
  imageUrl: string,
  width: number = 20
): Promise<string> => {
  return new Promise((resolve, reject) => {
    if (!imageUrl) {
      resolve("");
      return;
    }

    const img = new Image();
    img.crossOrigin = "anonymous";
    
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        
        if (!ctx) {
          resolve("");
          return;
        }

        // Calculate height maintaining aspect ratio
        const height = Math.round((img.height / img.width) * width);
        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to low-quality JPEG data URL
        const dataUrl = canvas.toDataURL("image/jpeg", 0.1);
        resolve(dataUrl);
      } catch (error) {
        console.warn("Failed to generate blur placeholder:", error);
        resolve("");
      }
    };

    img.onerror = () => {
      resolve("");
    };

    img.src = imageUrl;
  });
};

